import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  requestNotificationPermission,
  onMessageListener,
  getNotificationPermission,
  isFCMSupported,
  checkFCMCompatibility,
  manuallyRegisterServiceWorker,
  IS_FCM_ENABLED
} from '../services/firebase';
import axios from 'axios';
import '../styles/common.css';

function ChildSettings() {
  const [user, setUser] = useState(null);
  const [settings, setSettings] = useState({
    notifications: true,
    sound: true,
    dailyReminder: true,
    reminderTime: '18:00'
  });
  const [loading, setLoading] = useState(true);
  const [notificationStatus, setNotificationStatus] = useState({
    permission: 'default',
    supported: false,
    token: null
  });
  const [testLoading, setTestLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // 로그인 상태 확인
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      navigate('/child/login');
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);
      if (parsedUser.type !== 'child') {
        navigate('/');
        return;
      }
      setUser(parsedUser);
      // loadSettings(); // TODO: 실제 설정 로드
      checkNotificationStatus();
      setLoading(false);
    } catch (error) {
      console.error('사용자 데이터 파싱 오류:', error);
      navigate('/child/login');
    }
  }, [navigate]);

  // 알림 상태 확인
  const checkNotificationStatus = () => {
    setNotificationStatus({
      permission: getNotificationPermission(),
      supported: isFCMSupported(),
      token: null
    });
  };

  // 알림 권한 요청 및 토큰 수집 (개선된 버전)
  const handleEnableNotifications = async () => {
    try {
      setTestLoading(true);
      console.log('📱 알림 활성화 시작...');

      if (IS_FCM_ENABLED) {
        console.log('🔥 FCM 활성화됨, 토큰 요청 중...');

        // 브라우저 호환성 체크
        const compatibility = checkFCMCompatibility();
        console.log('🔍 FCM 호환성 체크:', compatibility);

        if (!compatibility.isSupported) {
          console.warn('⚠️ FCM 호환성 문제 발견:', compatibility.recommendations);

          // iOS Safari 사용자를 위한 특별 안내
          if (compatibility.userAgent.includes('iPhone') || compatibility.userAgent.includes('iPad')) {
            throw new Error('iOS에서는 홈 화면에 앱을 추가(PWA)한 후 사용해주세요.\n\n1. Safari 하단 공유 버튼 탭\n2. "홈 화면에 추가" 선택\n3. 추가된 앱에서 알림 설정');
          }

          // 다른 호환성 문제
          const recommendations = compatibility.recommendations.join('\n• ');
          throw new Error(`FCM을 사용할 수 없습니다:\n\n• ${recommendations}`);
        }

        // 실제 FCM 토큰 요청 (재시도 로직 포함)
        let token;
        let lastError;

        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            console.log(`📱 FCM 토큰 요청 시도 ${attempt}/3...`);
            token = await requestNotificationPermission();
            console.log('✅ FCM 토큰 요청 성공');
            break;

          } catch (error) {
            lastError = error;
            console.error(`❌ FCM 토큰 요청 실패 (시도 ${attempt}/3):`, error);

            // Service Worker 관련 오류인 경우 수동 등록 시도
            if (error.message.includes('서비스 워커') && attempt === 2) {
              try {
                console.log('🔧 Service Worker 수동 등록 시도...');
                await manuallyRegisterServiceWorker();
                console.log('✅ Service Worker 수동 등록 완료, FCM 재시도...');
                await new Promise(resolve => setTimeout(resolve, 2000)); // 2초 대기
              } catch (swError) {
                console.error('❌ Service Worker 수동 등록 실패:', swError);
              }
            }

            if (attempt < 3) {
              await new Promise(resolve => setTimeout(resolve, 1000)); // 1초 대기
            }
          }
        }

        if (!token) {
          throw lastError || new Error('모든 재시도가 실패했습니다.');
        }

        setNotificationStatus({
          permission: 'granted',
          supported: true,
          token: token
        });

        console.log('💾 서버에 토큰 저장 중...');

        // 서버에 토큰 저장
        const response = await axios.post('/api/v0/auth/notify/token', {
          token: token,
          platform: 'web'
        }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        console.log('✅ 서버 토큰 저장 완료:', response.data);
        alert('🎉 알림이 활성화되었습니다!');

        // 포그라운드 메시지 리스너 설정
        try {
          onMessageListener().then((payload) => {
            console.log('📥 포그라운드 메시지 수신:', payload);
          }).catch((error) => {
            console.log('⚠️ 메시지 리스너 오류:', error);
          });
        } catch (listenerError) {
          console.warn('메시지 리스너 설정 실패:', listenerError);
        }

      } else {
        throw new Error('FCM이 비활성화되어 있습니다.');
      }

    } catch (error) {
      console.error('❌ 알림 활성화 실패:', error);

      // 더 자세한 오류 메시지 제공
      let userMessage = '알림 활성화에 실패했습니다.';

      if (error.message.includes('권한이 거부')) {
        userMessage += '\n\n브라우저 설정에서 알림 권한을 허용해주세요:\n1. 주소창 왼쪽 자물쇠 아이콘 클릭\n2. 알림 → 허용 선택';
      } else if (error.message.includes('서비스 워커')) {
        userMessage += '\n\nService Worker 등록에 실패했습니다.\n페이지를 새로고침하고 다시 시도해주세요.';
      } else if (error.message.includes('Failed to fetch')) {
        userMessage += '\n\n네트워크 연결을 확인하고 다시 시도해주세요.';
      } else if (error.message.includes('HTTPS')) {
        userMessage += '\n\nHTTPS 연결이 필요합니다.';
      } else {
        userMessage += '\n\n' + error.message;
      }

      alert(userMessage);
    } finally {
      setTestLoading(false);
    }
  };

  // 테스트 알림 전송
  const handleTestNotification = async () => {
    try {
      setTestLoading(true);

      const response = await axios.post('/api/v0/auth/notify/test', {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.data.success) {
        if (response.data.development) {
          alert('🚧 푸시 알림 기능은 현재 개발 중입니다. 실제 알림은 곧 지원될 예정입니다.');
        } else {
          alert('📱 테스트 알림이 전송되었습니다!');
        }
      } else {
        alert('테스트 알림 전송에 실패했습니다.');
      }

    } catch (error) {
      console.error('테스트 알림 전송 실패:', error);
      alert('테스트 알림 전송에 실패했습니다.');
    } finally {
      setTestLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
    // TODO: 설정 저장 API 호출
  };


  if (!user) {
    return (
      <div className="page-container">
        <div className="card">
          <div style={{ textAlign: 'center', padding: '20px' }}>
            로딩 중...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* 헤더 */}
      <div className="page-header">
        <Link to="/child/dashboard" className="back-btn">← 뒤로가기</Link>
        <h1>⚙️ 설정</h1>
        <button onClick={handleLogout} className="btn btn-outline btn-sm">
          로그아웃
        </button>
      </div>

      <div className="page-content">
        {/* 프로필 카드 */}
        <div className="profile-card">
          <div className="profile-content">
            <div className="profile-avatar">👤</div>
            <div className="profile-info">
              <h3>{user.name}</h3>
              <p>가족: {user.familyName}</p>
            </div>
          </div>
        </div>

        {/* 알림 설정 */}
        <div className="settings-card">
          <h3>🔔 알림 설정</h3>
          <div className="settings-list">
            <div className="setting-item">
              <div className="setting-info">
                <h4>알림 받기</h4>
                <p>미션 완료, 승인 등의 알림을 받습니다</p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.notifications}
                  onChange={(e) => handleSettingChange('notifications', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <h4>알림 소리</h4>
                <p>알림이 올 때 소리를 재생합니다</p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.sound}
                  onChange={(e) => handleSettingChange('sound', e.target.checked)}
                  disabled={!settings.notifications}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <h4>일일 리마인더</h4>
                <p>매일 정해진 시간에 미션 알림을 받습니다</p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.dailyReminder}
                  onChange={(e) => handleSettingChange('dailyReminder', e.target.checked)}
                  disabled={!settings.notifications}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            {settings.dailyReminder && settings.notifications && (
              <div className="setting-item">
                <div className="setting-info">
                  <h4>리마인더 시간</h4>
                  <p>일일 리마인더를 받을 시간을 설정합니다</p>
                </div>
                <input
                  type="time"
                  value={settings.reminderTime}
                  onChange={(e) => handleSettingChange('reminderTime', e.target.value)}
                  className="time-input"
                />
              </div>
            )}
          </div>
        </div>

        {/* 푸시 알림 설정 */}
        <div className="settings-card">
          <h3>🔔 푸시 알림</h3>

          <div className="development-notice">
            <span className="dev-badge">개발 중</span>
            푸시 알림 기능은 현재 개발 중입니다. 곧 지원될 예정입니다.
          </div>

          <div className="notification-status">
            <div className="status-info">
              <p><strong>알림 지원:</strong> 🚧 개발 중</p>
              <p><strong>권한 상태:</strong> 🚧 개발 중</p>
            </div>

            <div className="notification-actions">
              <button
                disabled={true}
                className="btn btn-danger btn-full"
              >
                🚧 개발 중 - 사용 불가
              </button>
            </div>

            <div className="notification-help">
              <h4>📢 알림을 받는 경우 (개발 예정)</h4>
              <ul>
                <li>• 부모님이 미션을 승인/반려했을 때</li>
                <li>• 새로운 미션이 추가되었을 때</li>
                <li>• 용돈 정산이 완료되었을 때</li>
                <li>• 매일 미션 리마인더 (설정한 시간)</li>
              </ul>
              <p className="help-note">
                🚧 푸시 알림 기능은 현재 개발 중입니다. 곧 지원될 예정이니 조금만 기다려주세요!
              </p>
            </div>
          </div>
        </div>

        {/* 앱 정보 */}
        <div className="settings-card">
          <h3>ℹ️ 앱 정보</h3>
          <div className="info-list">
            <div className="info-item">
              <span className="info-label">앱 버전</span>
              <span className="info-value">1.0.0</span>
            </div>
            <div className="info-item">
              <span className="info-label">마지막 업데이트</span>
              <span className="info-value">2025년 9월</span>
            </div>
          </div>
        </div>

      </div>

      <style>{`
        .page-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding-bottom: 20px;
        }

        .page-header {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
          padding: 15px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }


        .page-header h1 {
          margin: 0;
          color: #764ba2;
          font-size: 1.3rem;
          font-weight: bold;
        }

        .page-content {
          max-width: 600px;
          margin: 0 auto;
          padding: 20px;
        }

        .profile-card, .settings-card {
          background: white;
          border-radius: 15px;
          padding: 20px;
          margin-bottom: 20px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.1);
        }

        .profile-content {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .profile-avatar {
          font-size: 3rem;
          width: 80px;
          height: 80px;
          background: #f8f9fa;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .profile-info h3 {
          margin: 0 0 5px 0;
          color: #333;
          font-size: 1.3rem;
        }

        .profile-info p {
          margin: 0;
          color: #666;
          font-size: 1rem;
        }

        .settings-card h3 {
          color: #333;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .settings-list {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .setting-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-bottom: 20px;
          border-bottom: 1px solid #f0f0f0;
        }

        .setting-item:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }

        .setting-info h4 {
          margin: 0 0 5px 0;
          color: #333;
          font-size: 1rem;
        }

        .setting-info p {
          margin: 0;
          color: #666;
          font-size: 0.9rem;
        }

        .toggle-switch {
          position: relative;
          display: inline-block;
          width: 50px;
          height: 28px;
        }

        .toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          transition: .4s;
          border-radius: 28px;
        }

        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 20px;
          width: 20px;
          left: 4px;
          bottom: 4px;
          background-color: white;
          transition: .4s;
          border-radius: 50%;
        }

        input:checked + .toggle-slider {
          background-color: #764ba2;
        }

        input:checked + .toggle-slider:before {
          transform: translateX(22px);
        }

        input:disabled + .toggle-slider {
          background-color: #e9ecef;
          cursor: not-allowed;
        }

        .time-input {
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 1rem;
          background: #f8f9fa;
        }

        .info-list {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .info-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 0;
          border-bottom: 1px solid #f0f0f0;
        }

        .info-item:last-child {
          border-bottom: none;
        }

        .info-label {
          color: #666;
        }

        .info-value {
          color: #333;
          font-weight: 500;
        }


        .btn-full {
          width: 100%;
        }

        /* 개발 중 알림 */
        .development-notice {
          background: linear-gradient(135deg, #ffc107 0%, #ff8c00 100%);
          color: white;
          padding: 15px;
          border-radius: 10px;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 10px;
          border: 2px solid #ff8c00;
        }

        .dev-badge {
          background: rgba(255,255,255,0.3);
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: bold;
          border: 1px solid rgba(255,255,255,0.4);
        }

        /* 푸시 알림 설정 스타일 */
        .notification-status {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .status-info p {
          margin: 8px 0;
          font-size: 0.9rem;
        }

        .demo-badge {
          color: #ff6b00;
          font-weight: bold;
          font-size: 0.85rem;
          background: #fff3e0;
          padding: 4px 8px;
          border-radius: 12px;
          display: inline-block;
          margin-top: 8px;
        }

        .notification-actions {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .notification-help {
          background: #f8f9fa;
          border-radius: 10px;
          padding: 15px;
          border-left: 4px solid #667eea;
        }

        .notification-help h4 {
          margin: 0 0 10px 0;
          color: #333;
          font-size: 1rem;
        }

        .notification-help ul {
          margin: 10px 0;
          padding-left: 20px;
        }

        .notification-help li {
          margin: 5px 0;
          color: #555;
          font-size: 0.9rem;
        }

        .help-note {
          margin: 12px 0 0 0;
          color: #666;
          font-size: 0.85rem;
          line-height: 1.4;
          padding-top: 10px;
          border-top: 1px solid #e0e0e0;
        }

        @media (max-width: 480px) {
          .profile-content {
            flex-direction: column;
            text-align: center;
            gap: 15px;
          }

          .setting-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }

          .toggle-switch {
            align-self: flex-end;
          }
        }
      `}</style>
    </div>
  );
}

export default ChildSettings;